import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  decrementCoins,
  setGameState,
  setCurrentMultiplier,
  resetGame,
} from "../store/gameStore";
import type { RootState } from "../store/gameStore";
import { GameState } from "../types/enums";
import { PlinkoBoard } from "./PlinkoBoard";
import { MultiplierSlots } from "./MultiplierSlots";
import { DropCoinButton } from "./DropCoinButton";
import { CoinCounter } from "./CoinCounter";
import { FloatingBubble } from "./FloatingBubble";
import { SoundToggle } from "./SoundToggle";
import { GameHeader } from "./GameHeader";
import { WhaleMascot } from "./WhaleMascot";
import { GameTitle } from "./GameTitle";
import { BigWinModal } from "./BigWinModal";
import {
  mockMultiplierSlotsDesktop,
  mockMultiplierSlotsTablet,
} from "../data/plinkoMockData";

export const PlinkoGame = () => {
  const dispatch = useDispatch();
  const { coins, gameState } = useSelector((state: RootState) => state.game);
  const [winningSlotIndex, setWinningSlotIndex] = useState<number | null>(null);
  const [showBigWin, setShowBigWin] = useState(false);
  const [triesCount, setTriesCount] = useState(0);
  const isSmallHeight = window.innerHeight < 700;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery("(max-width: 1800px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1200px)");
  const activeSlots = isTablet
    ? mockMultiplierSlotsTablet
    : mockMultiplierSlotsDesktop;
  // 🫧 Generisanje random bubble-a
  const floatingBubbles = useMemo(() => {
    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 1440,
      y: Math.random() * 810,
      size: Math.random() * 20 + 12,
      duration: Math.random() * 5 + 3,
      delay: Math.random() * 2,
    }));
  }, []);

  const handleDropCoin = () => {
    if (coins > 0 && gameState === GameState.IDLE) {
      dispatch(decrementCoins());
      dispatch(setGameState(GameState.DROPPING));
      setWinningSlotIndex(null);
    }
  };

  // 🎯 Target slot za 3. pokušaj
  const getTargetSlot = () => {
    if (triesCount === 2) {
      return 0; // 5FS slot
    }
    return undefined;
  };

  const handleCoinLanded = (slotIndex: number) => {
    setWinningSlotIndex(slotIndex);
    dispatch(setCurrentMultiplier(mockMultiplierSlotsDesktop[slotIndex].value));
    dispatch(setGameState(GameState.LANDED));

    const newTriesCount = triesCount + 1;
    setTriesCount(newTriesCount);

    // Reset nakon animacije
    setTimeout(() => {
      dispatch(setGameState(GameState.IDLE));
      setWinningSlotIndex(null);

      // Otvori Big Win nakon 3. pokušaja
      if (newTriesCount === 3) {
        setTimeout(() => {
          setShowBigWin(true);
        }, 500);
      }
    }, 2000);
  };

  const handleClaimPrize = () => {
    setShowBigWin(false);
    setTriesCount(0);
    dispatch(resetGame());
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        minHeight: isMobile ? "unset" : 750,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* 🌊 Pozadinska slika */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: "url(/bg-underwater.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: 0,
        }}
      />

      {/* 🫧 Floating bubbles */}
      {floatingBubbles.map((bubble) => (
        <FloatingBubble
          key={bubble.id}
          initialX={bubble.x}
          initialY={bubble.y}
          size={bubble.size}
          duration={bubble.duration}
          delay={bubble.delay}
        />
      ))}

      {/* 🧭 UI elementi */}
      <GameHeader />
      <SoundToggle />
      <WhaleMascot />
      <GameTitle />

      {/* 🕹 Gornji Stack — Plinko tabla + multiplieri */}
      <Stack
        spacing={
          isMobile ? 2 : -11.5 // 🖥 desktop spacing
        }
        alignItems="center"
        sx={{
          position: "absolute",
          top: isMobile
            ? isSmallHeight
              ? "42%" // 📱 pomjera tabla gore na malim ekranima (npr. iPhone SE)
              : "50%"
            : isSmallScreen
            ? "45%"
            : "48%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          mt: isMobile ? 2 : 4,
          zIndex: 4,
          width: isMobile ? "95%" : "auto",
          maxWidth: isMobile ? "100%" : "none",
        }}
      >
        <PlinkoBoard
          isDropping={gameState === GameState.DROPPING}
          onCoinLanded={handleCoinLanded}
          targetSlot={getTargetSlot()}
        />
        <Box
          sx={{
            position: "relative",
            scale: "1.0",
            left: "-4px",
            width: "auto",
            maxWidth: "none",
          }}
        >
          <MultiplierSlots
            slots={activeSlots}
            winningIndex={winningSlotIndex}
          />
        </Box>
      </Stack>

      {/* 🪙 Donji Stack — dugme i counter */}
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={isMobile ? 2 : 3}
        alignItems="center"
        justifyContent="center"
        sx={{
          position: "absolute",
          bottom: `calc(${
            isMobile ? "5vh" : "8vh"
          } + env(safe-area-inset-bottom))`,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 5,
          width: isMobile ? "90%" : "auto",
        }}
      >
        <DropCoinButton
          onClick={handleDropCoin}
          disabled={coins === 0 || gameState !== GameState.IDLE}
        />
        <CoinCounter coins={coins} />
      </Stack>

      {/* 🏆 Modal za big win */}
      <BigWinModal open={showBigWin} onClose={handleClaimPrize} freeSpins={5} />
    </Box>
  );
};
